/* eslint-disable @atlassian/tangerine/import/no-restricted-paths */
import type { PreloadedQuery } from 'react-relay';
import type { Match, RouteResource } from 'react-resource-router';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	HubBootstrapWrapperQuery,
	type hubBootstrapWrapperQuery,
	mockHubRouteProps,
} from '@atlassian/company-hub-common';
import RovoWidgetProviderQuery, {
	type rovoWidgetProviderQuery,
} from '@atlassian/rovo-service-agent/controllers/rovo-widget-provider/rovoWidgetProviderQuery';

import { HUB_PORTAL_PAGE_ROUTE_NAME, HUB_PREVIEW_ROUTE_NAME } from '../../controllers/constants';
import HubQuery, {
	type hubQuery as hubQueryType,
} from '../../ui/hub/__generated__/hubQuery.graphql';

import { createRelayResource, getPageAri } from './utils';

const getPageIdFromRouteParams = (match: Match, routeName?: string): string => {
	if (routeName === HUB_PORTAL_PAGE_ROUTE_NAME && fg('portal-pages-in-hub')) {
		const portalId = match.params.portalId;
		if (portalId) {
			return `portal_page-${portalId}`;
		}
	}
	return match.params.id || '';
};

export const hubBootstrapResource: RouteResource<
	PreloadedQuery<hubBootstrapWrapperQuery, Record<string, unknown>>
> = createRelayResource<hubBootstrapWrapperQuery>({
	getQuery: (
		{ match, route },
		{ cloudId, helpCenterAri, relayEnvironment, shouldAddEdgeFallbackToUnidentified },
	) => {
		const isPreview = route?.name === HUB_PREVIEW_ROUTE_NAME;
		const pageId = getPageIdFromRouteParams(match, route?.name);
		let cloudIdDerived = cloudId;
		let helpCenterAriDerived = helpCenterAri;

		if (isPreview && mockHubRouteProps.env.cloudId && mockHubRouteProps.env.helpCenterAri) {
			cloudIdDerived = mockHubRouteProps.env.cloudId;
			helpCenterAriDerived = mockHubRouteProps.env.helpCenterAri;
		}

		const skipPageMediaConfig = fg('hubs_fix_rerendering_bootstrap_page_media');

		const edgeFallbackHeaders =
			shouldAddEdgeFallbackToUnidentified && fg('csm_stargate_unidentified_fallback_on_bad_cookie')
				? { 'X-Edge-Fallback-To-Unidentified': 'true' }
				: undefined;

		return {
			variables: {
				cloudId: cloudIdDerived,
				hubAri: helpCenterAriDerived,
				// Always pass `$pageAri` so the query variable is always
				// defined; when `$skipPageMediaConfig` is true the field is
				// skipped by the GraphQL `@skip` directive and the value is
				// not sent to the server.
				pageAri: getPageAri(helpCenterAriDerived, pageId),
				operationType: 'READ' as const,
				hubEditMode: false,
				// When true, the bootstrap fragment skips `pageMediaConfig`
				// (via `@skip`) so the query stays page-agnostic and is not
				// refetched on every page navigation.
				skipPageMediaConfig,
			},
			query: HubBootstrapWrapperQuery,
			options: {
				fetchPolicy: 'store-or-network',
				...(edgeFallbackHeaders
					? { networkCacheConfig: { metadata: { headers: edgeFallbackHeaders } } }
					: {}),
			},
			environment: relayEnvironment,
		};
	},
});

export const hubPageResource: RouteResource<PreloadedQuery<hubQueryType, Record<string, unknown>>> =
	createRelayResource<hubQueryType>({
		getQuery: ({ match, route }, { cloudId, helpCenterAri, isHelpHub, relayEnvironment }) => {
			const isPreview = route?.name === HUB_PREVIEW_ROUTE_NAME;
			const pageId = getPageIdFromRouteParams(match, route?.name);
			let cloudIdDerived = cloudId;
			let helpCenterAriDerived = helpCenterAri;

			if (isPreview && mockHubRouteProps.env.cloudId && mockHubRouteProps.env.helpCenterAri) {
				cloudIdDerived = mockHubRouteProps.env.cloudId;
				helpCenterAriDerived = mockHubRouteProps.env.helpCenterAri;
			}

			return {
				variables: {
					cloudId: cloudIdDerived,
					hubAri: helpCenterAriDerived,
					pageAri: getPageAri(helpCenterAriDerived, pageId),
					operationType: 'READ',
					// Gates the page-scoped `rovoConfig` selection. When false the field is not even
					// requested, so help-kosh never resolves it and no call reaches convo-ai — the
					// existing `rovoWidgetProviderQuery` path stays in sole use. Skipped unless the
					// callout banner could use the answer, which rules out CSM support sites.
					rovoConfigFromKosh: Boolean(isHelpHub) && fg('hubs_rovo_config_from_kosh'),
				},
				environment: relayEnvironment,
				query: HubQuery,
				options: {
					fetchPolicy: 'store-or-network',
				},
			};
		},
	});

/**
 * Preloads the Rovo widget query at route-resolution time (in parallel with the
 * hub bootstrap/page queries) so the help-hub callout banner's "Ask Rovo" search
 * box can render without waiting on a late, lazily-fired query.
 *
 * This resource is only attached to a route when the `hub_preload_rovo_widget_query`
 * gate is on — the gating lives at the route level (see the route factories in
 * `../../controllers`), not inside this resource. When it is not attached, the
 * router never loads it and `useResource` simply reads `null`.
 */
export const rovoWidgetResource: RouteResource<
	PreloadedQuery<rovoWidgetProviderQuery, Record<string, unknown>>
> = createRelayResource<rovoWidgetProviderQuery>({
	getQuery: ({ route }, { cloudId, helpCenterAri, relayEnvironment }) => {
		const isPreview = route?.name === HUB_PREVIEW_ROUTE_NAME;
		let cloudIdDerived = cloudId;
		let helpCenterAriDerived = helpCenterAri;

		if (isPreview && mockHubRouteProps.env.cloudId && mockHubRouteProps.env.helpCenterAri) {
			cloudIdDerived = mockHubRouteProps.env.cloudId;
			helpCenterAriDerived = mockHubRouteProps.env.helpCenterAri;
		}

		return {
			variables: {
				cloudId: cloudIdDerived,
				cloudIdString: cloudIdDerived,
				containerAri: helpCenterAriDerived,
			},
			query: RovoWidgetProviderQuery,
			options: {
				fetchPolicy: 'store-or-network',
			},
			environment: relayEnvironment,
		};
	},
});
